/**
 * Agent Processes Resource
 *
 * Thin 1:1 mapping to the /v1/agent-processes API (RPC-style POST
 * endpoints). No business logic, no default parameter injection.
 */

import type { HttpLayer } from '../http.js';
import type {
  AgentProcess,
  CreateAgentProcessRequest,
  UpdateAgentProcessRequest,
  ProcessDefinition,
  ValidateDefinitionResult,
  CopyToAccountRequest,
  CopyToAccountResult,
  StepTemplate,
  CreateStepTemplateRequest,
  UpdateStepTemplateRequest,
} from '../types/index.js';

const prefix = '/v1/agent-processes';

/** Step-template sub-resource (system + account step presets). */
export class AgentProcessStepTemplatesResource {
  constructor(private http: HttpLayer) {}

  async create(params: CreateStepTemplateRequest): Promise<StepTemplate> {
    return this.http.post(`${prefix}/steps/create`, params);
  }

  async list(): Promise<StepTemplate[]> {
    return this.http.post(`${prefix}/steps/list`, {});
  }

  async get(stepTemplateId: string): Promise<StepTemplate> {
    return this.http.post(`${prefix}/steps/get`, {
      step_template_id: stepTemplateId,
    });
  }

  async update(
    stepTemplateId: string,
    params: UpdateStepTemplateRequest,
  ): Promise<StepTemplate> {
    return this.http.post(`${prefix}/steps/update`, {
      step_template_id: stepTemplateId,
      ...params,
    });
  }

  async delete(stepTemplateId: string): Promise<{ success: boolean }> {
    return this.http.post(`${prefix}/steps/delete`, {
      step_template_id: stepTemplateId,
    });
  }
}

export class AgentProcessesResource {
  readonly steps: AgentProcessStepTemplatesResource;

  constructor(private http: HttpLayer) {
    this.steps = new AgentProcessStepTemplatesResource(http);
  }

  async create(params: CreateAgentProcessRequest): Promise<AgentProcess> {
    return this.http.post(`${prefix}/create`, params);
  }

  async list(): Promise<AgentProcess[]> {
    return this.http.post(`${prefix}/list`, {});
  }

  async get(processId: string): Promise<AgentProcess> {
    return this.http.post(`${prefix}/get`, { process_id: processId });
  }

  async update(
    processId: string,
    params: UpdateAgentProcessRequest,
  ): Promise<AgentProcess> {
    return this.http.post(`${prefix}/update`, {
      process_id: processId,
      ...params,
    });
  }

  async delete(processId: string): Promise<{ success: boolean }> {
    return this.http.post(`${prefix}/delete`, { process_id: processId });
  }

  /** Validate a definition without saving. */
  async validate(
    definition: ProcessDefinition | Record<string, unknown>,
  ): Promise<ValidateDefinitionResult> {
    return this.http.post(`${prefix}/validate`, { definition });
  }

  /** Copy a system process or system step template into the account library. */
  async copyToAccount(
    params: CopyToAccountRequest,
  ): Promise<CopyToAccountResult> {
    return this.http.post(`${prefix}/copy`, params);
  }
}
